
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { usePermissions } from "@/context/PermissionsContext";
import { Resource } from "@/hooks/rollenbeheer/useRolePermissions";
import { UserRole } from "@/context/AuthContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { 
  Home, 
  Database, 
  Bell, 
  BarChart3, 
  Users,
  ShieldCheck,
  MessageSquare,
  LayoutDashboard,
  Package,
  UserPlus,
  Workflow,
  BookOpen,
  AlertTriangle,
  KeyRound,
  PackagePlus,
  ShoppingCart,
} from "lucide-react";
import { 
  SidebarMenu, 
  SidebarMenuItem, 
  SidebarMenuButton
} from "@/components/ui/sidebar";

// Menu item type definition
interface MenuItem {
  title: string;
  icon: React.ElementType;
  path: string;
  resource: Resource;
  requireManagement?: boolean;
  permissionType?: "read" | "write" | "delete";
}

// List of all menu items with their paths for getting page titles
export const menuItems: MenuItem[] = [
  { title: "Dashboard", icon: Home, path: "/dashboard", resource: "dashboard" },
  { title: "Voorraad", icon: Database, path: "/inventory", resource: "inventory" },
  { title: "Meldingen", icon: Bell, path: "/notifications", resource: "notifications" },
  { title: "Feedback", icon: MessageSquare, path: "/feedback", resource: "feedback" },
  { title: "Pakket Ophaaltijden", icon: Package, path: "/package-pickup", resource: "packagePickup" },
  { title: "Rapportages", icon: BarChart3, path: "/reports", resource: "reports" },
  { title: "Medewerkers", icon: Users, path: "/medewerkers", resource: "medewerkers" },
  { title: "Rollenbeheer", icon: ShieldCheck, path: "/rollenbeheer", resource: "medewerkers", requireManagement: true },
  { title: "Gebruikersbeheer", icon: KeyRound, path: "/admin/gebruikers", resource: "medewerkers", requireManagement: true },
  { title: "Registratie Aanvragen", icon: UserPlus, path: "/admin/registraties", resource: "medewerkers", requireManagement: true },
  { title: "Nieuws+", icon: LayoutDashboard, path: "/modal-campagnes", resource: "medewerkers", requireManagement: true },
  { title: "Workflow Executions", icon: Workflow, path: "/workflow-executions", resource: "medewerkers", requireManagement: true },
  { title: "Vervallen Producten", icon: AlertTriangle, path: "/expired-products", resource: "medewerkers", requireManagement: true },
  { title: "Nieuwe Producten", icon: PackagePlus, path: "/nieuwe-producten", resource: "medewerkers", requireManagement: true },
  { title: "Orders", icon: ShoppingCart, path: "/orders", resource: "medewerkers", requireManagement: true },
  { title: "API Documentatie", icon: BookOpen, path: "/api-docs", resource: "medewerkers", requireManagement: true },
];

const MenuItems = ({ role }: { role: UserRole }) => {
  const { hasPermission } = usePermissions();
  const isMobile = useIsMobile();
  
  // Debug log for user role
  useEffect(() => {
    console.log("MenuItems rendering for role:", role);
  }, [role]);
  
  // Filter items based on permissions
  const allowedItems = menuItems.filter(item => {
    // Special case for management-only features
    if (item.requireManagement && role !== "management") {
      console.log(`${item.title} requires management role, user has ${role}`);
      return false;
    }
    
    const hasAccess = hasPermission(item.resource, item.permissionType || "read");
    console.log(`Menu item permission check for ${item.title} (${item.resource}): ${hasAccess}`);
    return hasAccess;
  });

  return (
    <>
      {allowedItems.map((item) => (
        <SidebarMenuItem key={item.title}>
          <SidebarMenuButton asChild>
            <Link to={item.path} className="flex items-center">
              <item.icon className="w-5 h-5 mr-3" />
              <span>{item.title}</span>
            </Link>
          </SidebarMenuButton>
        </SidebarMenuItem>
      ))}
    </>
  );
};

export default MenuItems;

